import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { perfStart } from './utils/perf.ts';
import { applyPerfTier } from './utils/perfTier.ts';
import { initDebugMode } from './hooks/useDebugMode.ts';

// Detecta capability do device pra CSS condicional (Liquid Glass etc).
// Roda ANTES do render pra evitar flash de estilo pesado em device fraco.
applyPerfTier();

// Detecta `?debug=1` ou `?debug=0` na URL pra ativar/desativar QA panel.
// Limpa o param da URL após ler — links compartilhados não viralizam debug mode.
initDebugMode();

// Sentry init é deferido pra depois do primeiro paint via idle callback.
// Antes rodava sincrono no boot, atrasando ~30-80ms o TTI em devices fracos.
// Erros que acontecerem antes do init ainda são capturados via window.onerror
// fallback do próprio Sentry quando ele inicializar.
const initSentryDeferred = () => {
  void import('./utils/sentry.ts').then((m) => m.initSentry());
};
if (typeof window !== 'undefined') {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(initSentryDeferred, { timeout: 4000 });
  } else {
    window.setTimeout(initSentryDeferred, 2000);
  }
}

perfStart('app:start');

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);

// Esconde o splash estático do index.html assim que o React assumir o render.
// Faz fade-out suave (definido via CSS no #app-splash.hide).
function hideStaticSplash() {
  const splash = document.getElementById('app-splash');
  if (!splash) return;
  // Aguarda 1 frame para garantir que o React já pintou algo no #root.
  requestAnimationFrame(() => {
    splash.classList.add('hide');
    // Remove do DOM depois da transição para liberar memória.
    setTimeout(() => splash.remove(), 500);
  });
}

if (document.readyState === 'complete') {
  hideStaticSplash();
} else {
  window.addEventListener('load', hideStaticSplash, { once: true });
}

// Service Worker registration is handled automatically by vite-plugin-pwa
// via virtual:pwa-register in production builds. No manual code needed here.
